/**
 * Fingerprint (fingerprint.com) visitor identification — browser side.
 *
 * The agent public key is NOT a secret: it is meant to ship in the browser
 * bundle. When it is empty, identification is skipped and the app falls back to
 * the local browser identifier (bv_…) + IP.
 *
 * Optional first-party proxy (Cloudflare integration):
 *   VITE_FINGERPRINT_SCRIPT_URL_PATTERN → agent download through our own domain
 *   VITE_FINGERPRINT_ENDPOINT          → identification requests through our own domain
 * Both are optional and additive: when absent, the direct EU configuration is
 * kept unchanged.
 *
 * Honest limitation about the budget: once `getVisitorData()` has been started,
 * the SDK may finish its own in-flight network request after our deadline. We
 * cannot cancel it. What we guarantee is that no step is *started* after the
 * deadline, and that a late result is never consumed, transmitted or applied.
 */
import {
  FpjsClient,
  defaultEndpoint,
  defaultScriptUrlPattern,
} from "@fingerprintjs/fingerprintjs-pro-spa";

export const FINGERPRINT_PUBLIC_KEY =
  (import.meta.env.VITE_FINGERPRINT_PUBLIC_KEY as string | undefined) ?? "";

const SCRIPT_URL_PATTERN =
  (import.meta.env.VITE_FINGERPRINT_SCRIPT_URL_PATTERN as string | undefined) ?? "";
const ENDPOINT = (import.meta.env.VITE_FINGERPRINT_ENDPOINT as string | undefined) ?? "";

/** Kept below the 5 minutes accepted by the server-side event verification. */
export const FINGERPRINT_CACHE_SECONDS = 240;

/** Global budget for one identification chain. Never extended. */
export const FINGERPRINT_BUDGET_MS = 8000;

export type FingerprintStatus =
  | "success"
  | "missing_key"
  | "agent_load_error"
  | "identification_error"
  | "timeout"
  | "no_request_id";

export type FingerprintOutcome = {
  status: FingerprintStatus;
  /** Present only when status === 'success'. */
  requestId: string | null;
  visitorId: string | null;
  /** Identification attempts actually started, timeouts included. */
  attempts: number;
  durationMs: number;
  /** True when the proxy configuration is active for this page. */
  proxied: boolean;
};

export type FingerprintLogContext = {
  traceId?: string | null;
  sessionId?: string | null;
};

export const isProxyConfigured = Boolean(SCRIPT_URL_PATTERN || ENDPOINT);

function buildLoadOptions() {
  const options: Record<string, unknown> = {
    apiKey: FINGERPRINT_PUBLIC_KEY,
    region: "eu",
  };
  if (SCRIPT_URL_PATTERN) {
    options.scriptUrlPattern = [SCRIPT_URL_PATTERN, defaultScriptUrlPattern];
  }
  if (ENDPOINT) {
    options.endpoint = [ENDPOINT, defaultEndpoint];
  }
  return options as ConstructorParameters<typeof FpjsClient>[0]["loadOptions"];
}

/** Normalised, message-free diagnostic entry (never a raw SDK message). */
function logDiagnostic(entry: Record<string, unknown>) {
  try {
    console.info("fingerprint.client", JSON.stringify(entry));
  } catch {
    /* never break the page for a log */
  }
}

// ---------------------------------------------------------------------------
// Chain state: one chain per identification budget window. Every reference is
// released on expiry with an identity guard, so a hung init can never block the
// next navigation and an old promise never clears a newer chain.
// ---------------------------------------------------------------------------

type ChainState = {
  id: number;
  startedAt: number;
  deadline: number;
  attempts: number;
  promise: Promise<FingerprintOutcome>;
};

type ClientRef = {
  chainId: number;
  ready: boolean;
  promise: Promise<FpjsClient>;
};

let nextChainId = 1;
let chain: ChainState | null = null;
let clientRef: ClientRef | null = null;

/** Test seam: drop every cached reference. */
export function __resetFingerprintState() {
  chain = null;
  clientRef = null;
}

function releaseChain(chainId: number) {
  if (chain?.id === chainId) chain = null;
  // A client whose init never settled belongs to that expired chain: drop it so
  // a later navigation initialises a fresh one. A ready client is kept.
  if (clientRef && clientRef.chainId === chainId && !clientRef.ready) clientRef = null;
}

function getClient(chainId: number): Promise<FpjsClient> {
  if (clientRef) return clientRef.promise;
  const client = new FpjsClient({
    loadOptions: buildLoadOptions(),
    cacheTimeInSeconds: FINGERPRINT_CACHE_SECONDS,
  });
  const ref: ClientRef = {
    chainId,
    ready: false,
    promise: client
      .init()
      .then(() => {
        if (clientRef === ref) ref.ready = true;
        return client;
      })
      .catch((error) => {
        // Release the cached promise so a later navigation can retry a fresh init.
        if (clientRef === ref) clientRef = null;
        throw error;
      }),
  };
  clientRef = ref;
  return ref.promise;
}

type Attempt = {
  ok: boolean;
  status: Exclude<FingerprintStatus, "missing_key">;
  requestId: string | null;
  visitorId: string | null;
};

const failure = (status: Attempt["status"]): Attempt => ({
  ok: false,
  status,
  requestId: null,
  visitorId: null,
});

async function runAttempt(state: ChainState, ignoreCache: boolean): Promise<Attempt> {
  const expired = () => Date.now() >= state.deadline;
  if (expired()) return failure("timeout");

  state.attempts += 1;
  let client: FpjsClient;
  try {
    client = await getClient(state.id);
  } catch {
    return failure("agent_load_error");
  }
  // Checked again after the await and before starting a new step.
  if (expired()) return failure("timeout");

  try {
    const result = await client.getVisitorData({}, ignoreCache);
    if (expired()) return failure("timeout");
    if (!result?.requestId) return failure("no_request_id");
    return {
      ok: true,
      status: "success",
      requestId: result.requestId,
      visitorId: result.visitorId ?? null,
    };
  } catch {
    return failure("identification_error");
  }
}

async function runChain(state: ChainState): Promise<FingerprintOutcome> {
  const first = await runAttempt(state, false);

  // One bounded retry (cache bypass) on load error, identification error or
  // incomplete result — its result is the one actually consumed.
  let attempt = first;
  if (!first.ok && first.status !== "timeout" && Date.now() < state.deadline) {
    attempt = await runAttempt(state, true);
  }

  const durationMs = Date.now() - state.startedAt;
  return {
    proxied: isProxyConfigured,
    status: attempt.ok ? "success" : attempt.status,
    requestId: attempt.ok ? attempt.requestId : null,
    visitorId: attempt.ok ? attempt.visitorId : null,
    attempts: state.attempts,
    durationMs,
  };
}

/**
 * Resolves an identification outcome. Never rejects and never waits longer than
 * `budgetMs`. No step is started after the deadline and a late result is never
 * consumed (see the limitation documented at the top of this file).
 * Concurrent callers within the same window share one identification.
 */
export async function identifyVisitor(
  budgetMs = FINGERPRINT_BUDGET_MS,
  ctx: FingerprintLogContext = {},
): Promise<FingerprintOutcome> {
  const startedAt = Date.now();

  if (!FINGERPRINT_PUBLIC_KEY) {
    const outcome: FingerprintOutcome = {
      status: "missing_key",
      requestId: null,
      visitorId: null,
      attempts: 0,
      durationMs: 0,
      proxied: isProxyConfigured,
    };
    logDiagnostic({
      step: "identify",
      status: outcome.status,
      attempts: 0,
      trace_id: ctx.traceId ?? null,
      session_id: ctx.sessionId ?? null,
    });
    return outcome;
  }

  if (!chain || Date.now() >= chain.deadline) {
    if (chain) releaseChain(chain.id);
    const state: ChainState = {
      id: nextChainId++,
      startedAt,
      deadline: startedAt + budgetMs,
      attempts: 0,
      promise: Promise.resolve({} as FingerprintOutcome),
    };
    state.promise = runChain(state).finally(() => releaseChain(state.id));
    chain = state;
  }
  const active = chain;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<FingerprintOutcome>((resolve) => {
    timer = setTimeout(
      () => {
        releaseChain(active.id);
        resolve({
          status: "timeout",
          requestId: null,
          visitorId: null,
          attempts: active.attempts,
          durationMs: Date.now() - startedAt,
          proxied: isProxyConfigured,
        });
      },
      Math.max(active.deadline - Date.now(), 0),
    );
  });

  const outcome = await Promise.race([active.promise, timeout]);
  if (timer) clearTimeout(timer);

  logDiagnostic({
    step: "identify",
    status: outcome.status,
    attempts: outcome.attempts,
    duration_ms: outcome.durationMs,
    has_request_id: Boolean(outcome.requestId),
    proxied: outcome.proxied,
    region: "eu",
    trace_id: ctx.traceId ?? null,
    session_id: ctx.sessionId ?? null,
  });

  return outcome;
}

/** Client status → wire value stored as a diagnostic hint only. */
export function clientStatusForWire(outcome: FingerprintOutcome): string {
  return `client_${outcome.status}`;
}

// ---------------------------------------------------------------------------
// Login proof identification.
//
// Deliberately ISOLATED from the navigation chain above: its own client, no
// cache, no shared in-flight promise. That way it never reuses an anonymous
// observation, never counts an extra page view or click, and the nonce tag is
// guaranteed to belong to this identification only. Same 8 s budget, and every
// failure is non-blocking for the user.
// ---------------------------------------------------------------------------

export type ProofOutcome = {
  status: FingerprintStatus;
  requestId: string | null;
};

export async function identifyForLoginProof(
  nonce: string,
  budgetMs = FINGERPRINT_BUDGET_MS,
): Promise<ProofOutcome> {
  if (!FINGERPRINT_PUBLIC_KEY) return { status: "missing_key", requestId: null };

  const deadline = Date.now() + budgetMs;
  const run = async (): Promise<ProofOutcome> => {
    const client = new FpjsClient({
      loadOptions: buildLoadOptions(),
      cacheTimeInSeconds: 0,
    });
    try {
      await client.init();
    } catch {
      return { status: "agent_load_error", requestId: null };
    }
    if (Date.now() >= deadline) return { status: "timeout", requestId: null };
    try {
      const result = await client.getVisitorData({ tag: { fpChallenge: nonce } }, true);
      if (Date.now() >= deadline) return { status: "timeout", requestId: null };
      if (!result?.requestId) return { status: "no_request_id", requestId: null };
      return { status: "success", requestId: result.requestId };
    } catch {
      return { status: "identification_error", requestId: null };
    }
  };

  const timeout = new Promise<ProofOutcome>((resolve) => {
    setTimeout(() => resolve({ status: "timeout", requestId: null }), Math.max(deadline - Date.now(), 0));
  });

  const outcome = await Promise.race([run(), timeout]);
  logDiagnostic({ step: "login_proof", status: outcome.status, has_request_id: Boolean(outcome.requestId) });
  return outcome;
}
